import axios from "axios";

import { cancelTokenRefresh } from "./interceptor";

import { getUserCredentials } from "../utils/common";
import { API_URL_AUTH, API_URL } from "../utils/Constant";

let isLoggingOut = false;

const buildLogoutUrl = () => {
  if (API_URL_AUTH) {
    return `${API_URL_AUTH}/logout/`;
  }
  if (API_URL) {
    return `${API_URL}/logout/`;
  }
  return "/logout/";
};

/**
 * Check if logout is currently in progress
 * @returns {boolean} true if logout is in progress
 */
export const isLogoutInProgress = () => isLoggingOut;

/**
 * Call backend logout endpoint to invalidate Firebase token.
 * This function should be called before signOut() to ensure backend token invalidation.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
const logout = async () => {
  // Prevent concurrent logout calls
  if (isLoggingOut) {
    return {
      success: false,
      message: "Logout already in progress",
    };
  }

  isLoggingOut = true;

  try {
    // Cancel any in-progress token refresh to prevent unnecessary API calls
    cancelTokenRefresh();

    const userCredentials = getUserCredentials();

    // If no credentials, consider logout successful (already logged out)
    if (!userCredentials) {
      return {
        success: true,
        message: "No credentials found, already logged out",
      };
    }

    const companyDomain = userCredentials?.company_domain;
    const userEmail = userCredentials?.user_email;
    const firebaseIdToken = userCredentials?.firebase_id_token;
    const firebaseRefreshToken = userCredentials?.firebase_refresh_token;

    if (!companyDomain || !userEmail || !firebaseIdToken) {
      // Missing credentials but user might already be logged out
      return {
        success: true,
        message: "Missing credentials, proceeding with local logout",
      };
    }

    const payload = {
      company_domain: companyDomain,
      user_email: userEmail,
      firebase_id_token: firebaseIdToken,
      disableInterceptor: true,
    };

    if (firebaseRefreshToken) {
      payload.firebase_refresh_token = firebaseRefreshToken;
    }

    try {
      const response = await axios.post(buildLogoutUrl(), payload, {
        timeout: 5000, // 5 second timeout
      });

      const { message } = response.data || {};
      const isSuccess = response.status === 200 && (
        !message
        || message === "Success"
        || message.toLowerCase().includes("success")
        || message.toLowerCase().includes("logged out")
      );

      return {
        success: isSuccess,
        message: message || "Logout successful",
      };
    } catch (error) {
      // Network errors or API failures shouldn't block logout
      // Log the error but still return success to allow local cleanup
      const message = error.response?.data?.message ?? error.message ?? "Logout API call failed";

      // If it's a network error or timeout, still allow logout to proceed
      const isNetworkError = !error.response || error.code === "ECONNABORTED";

      return {
        success: isNetworkError, // Allow logout on network errors
        message: isNetworkError ? "Network error, proceeding with local logout" : message,
      };
    }
  } finally {
    isLoggingOut = false;
  }
};

export default logout;
